import React, { useState, useMemo } from "react";
import "../../css/calculator.scss";
import Header from "../common/Header";
import Footer from "../common/Footer";
import { useTranslation } from "react-i18next";

const Calculator = () => {
  const { t } = useTranslation();
  const [syringe, setSyringe] = useState(0.3);
  const [vialMg, setVialMg] = useState(5);
  const [waterMl, setWaterMl] = useState(1);
  const [doseMg, setDoseMg] = useState(0.5);
  const [showVialOther, setShowVialOther] = useState(false);
  const [showWaterOther, setShowWaterOther] = useState(false);
  const [showDoseOther, setShowDoseOther] = useState(false);
  const [vialOtherInput, setVialOtherInput] = useState("");
  const [waterOtherInput, setWaterOtherInput] = useState("");
  const [doseOtherInput, setDoseOtherInput] = useState("");

  const sanitizeLeadingZeros = (s) => {
    if (s == null) return "";
    let t = String(s).replace(",", ".").trim();
    if (t === "") return "";
    // Keep "0.x" but strip leading zeros on integers like "08"
    if (/^\d+(\.\d+)?$/.test(t)) {
      const [intPart, fracPart] = t.split(".");
      const intNormalized = intPart.replace(/^0+(\d)/, "$1");
      t = fracPart != null ? `${intNormalized}.${fracPart}` : intNormalized;
    }
    return t;
  };

  const capacityUnits = useMemo(() => {
    if (syringe === 0.3) return 30;
    if (syringe === 0.5) return 50;
    return 100;
  }, [syringe]);

  const concentrationMgPerMl = useMemo(() => {
    if (!waterMl || !vialMg) return 0;
    return vialMg / waterMl;
  }, [vialMg, waterMl]);

  const neededMl = useMemo(() => {
    if (!concentrationMgPerMl) return 0;
    return doseMg / concentrationMgPerMl;
  }, [doseMg, concentrationMgPerMl]);

  // 1 ml = 100 units; 结果单位不随注射器变
  const units = useMemo(() => Math.round(neededMl * 100), [neededMl]);
  // 仅用于进度条可视化的截断，不影响文本显示
  const unitsClamped = Math.min(Math.max(units, 0), capacityUnits);

  return (
    <div>
      <Header />
      <main className="calc-page">
        <div className="calc__container">
          <h1 className="calc__title">{t("calculator.title")}</h1>

          <section className="calc-card" aria-label={t("calculator.aria.section")}>
            <div className="calc__grid">
              <div className="calc__group">
                <div className="calc__label">{t("calculator.labels.syringeVolume")}</div>
                <div className="syr-list">
                  {[0.3, 0.5, 1.0].map((v) => (
                    <button
                      key={`syr-${v}`}
                      className={`syr-item ${syringe === v ? "active" : ""}`}
                      onClick={() => setSyringe(v)}
                    >
                      <p className="syr-item__label">{v} ml</p>
                      <img
                        alt={t("calculator.alt.syringe", { v })}
                        src={
                          v === 0.3
                            ? "/main/c1-1.png"
                            : v === 0.5
                            ? "/main/c1-2.png"
                            : "/main/c1-3.png"
                        }
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div className="calc__group">
                <div className="group-row">
                  <img className="group-thumb" alt={t("calculator.alt.vial")} src="/main/c2-1.avif" />
                  <div className="group-body">
                    <div className="calc__label">{t("calculator.labels.vialMg")}</div>
                    <div className="btn-row">
                      {[5, 10, 15].map((m) => (
                        <button
                          key={`vial-${m}`}
                          className={`opt-btn ${!showVialOther && vialMg === m ? "active" : ""}`}
                          onClick={() => {
                            setShowVialOther(false);
                            setVialMg(m);
                          }}
                        >
                          {m} mg
                        </button>
                      ))}
                      <button
                        className={`opt-btn ${showVialOther ? "active" : ""}`}
                        onClick={() => {
                          setShowVialOther(true);
                          setVialOtherInput(String(vialMg));
                        }}
                      >
                        {t("calculator.buttons.other")}
                      </button>
                      {showVialOther && (
                        <div className="opt-other show">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={vialOtherInput}
                            onChange={(e) => {
                              const v = sanitizeLeadingZeros(e.target.value);
                              setVialOtherInput(v);
                              const n = parseFloat(v);
                              if (!Number.isNaN(n)) setVialMg(n);
                            }}
                          />
                          <span className="suffix">mg</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="group-row mt">
                  <img className="group-thumb" alt={t("calculator.alt.water")} src="/main/c2-2.avif" />
                  <div className="group-body">
                    <div className="calc__label">{t("calculator.labels.waterMl")}</div>
                    <div className="btn-row">
                      {[1, 2, 3, 5].map((m) => (
                        <button
                          key={`water-${m}`}
                          className={`opt-btn ${!showWaterOther && waterMl === m ? "active" : ""}`}
                          onClick={() => {
                            setShowWaterOther(false);
                            setWaterMl(m);
                          }}
                        >
                          {m} ml
                        </button>
                      ))}
                      <button
                        className={`opt-btn ${showWaterOther ? "active" : ""}`}
                        onClick={() => {
                          setShowWaterOther(true);
                          setWaterOtherInput(String(waterMl));
                        }}
                      >
                        {t("calculator.buttons.other")}
                      </button>
                      {showWaterOther && (
                        <div className="opt-other show">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={waterOtherInput}
                            onChange={(e) => {
                              const v = sanitizeLeadingZeros(e.target.value);
                              setWaterOtherInput(v);
                              const n = parseFloat(v);
                              if (!Number.isNaN(n)) setWaterMl(n);
                            }}
                          />
                          <span className="suffix">ml</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="calc__label mt">{t("calculator.labels.doseMg")}</div>
                <div className="btn-row">
                  {[0.5, 1, 2, 3].map((m) => (
                    <button
                      key={`dose-${m}`}
                      className={`opt-btn ${!showDoseOther && doseMg === m ? "active" : ""}`}
                      onClick={() => {
                        setShowDoseOther(false);
                        setDoseMg(m);
                      }}
                    >
                      {m} mg
                    </button>
                  ))}
                  <button
                    className={`opt-btn ${showDoseOther ? "active" : ""}`}
                    onClick={() => {
                      setShowDoseOther(true);
                      setDoseOtherInput(String(doseMg));
                    }}
                  >
                    {t("calculator.buttons.other")}
                  </button>
                  {showDoseOther && (
                    <div className="opt-other show">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={doseOtherInput}
                        onChange={(e) => {
                          const v = sanitizeLeadingZeros(e.target.value);
                          setDoseOtherInput(v);
                          const n = parseFloat(v);
                          if (!Number.isNaN(n)) setDoseMg(n);
                        }}
                      />
                      <span className="suffix">mg</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="calc__result">
              <div className="result-text">
                {t("calculator.result.prefix")} <strong>{doseMg} mg</strong> {t("calculator.result.suffix")}{" "}
                <strong>{isFinite(units) ? units : 0}</strong>
              </div>
              <div className="slider">
                <div
                  className="slider__fill"
                  style={{ width: `${(unitsClamped / capacityUnits) * 100}%` }}
                />
              </div>
              <div className="slider__scale">
                {[0, capacityUnits / 6, (capacityUnits / 6) * 2, (capacityUnits / 6) * 3, (capacityUnits / 6) * 4, (capacityUnits / 6) * 5, capacityUnits].map(
                  (n, idx) => (
                    <span key={`mark-${idx}`}>{Math.round(n)}</span>
                  )
                )}
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Calculator;
